// Legacy grid on old sites. Never add it. Use multihub-floorplans.

/**
 * Floor Plans Block
 *
 * Displays property floor plans from the floorplans collection.
 * Supports two layout variants: "editorial" (side-by-side image/info)
 * and "card" (stacked card grid). All text and layout options are
 * parameterized via block tokens.
 *
 * Inherits theme styling through CSS variables and Tailwind utility classes.
 */


import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, EditableBlockToken } from '@vibesites/runtime/react';
import { useFloorplans } from '@vibesites/runtime/react';
import { defineComponentRequirements, collections } from '@vibesites/tokens';
import { Button, Card, CardContent, CardHeader, CardTitle, VibesitesImage, cn } from '@vibesites/ui-primitives';

// ============================================================================
// Component
// ============================================================================

function FloorplansComponent({ sectionId: _sectionId }: BlockProps) {
  const allFloorplans = useFloorplans();

  const title = useBlockToken('title', { defaultValue: 'Floor Plans' });
  const subtitle = useBlockToken('subtitle', { defaultValue: '' });
  const layout = useBlockToken('layout', { defaultValue: 'card' });
  const limitStr = useBlockToken('limit', { defaultValue: '6' });
  const showHeader = useBlockToken('show_header', { defaultValue: 'true' });

  const limit = parseInt(limitStr || '6', 10) || 6;
  const floorplans = limit > 0 ? allFloorplans.slice(0, limit) : allFloorplans;
  const headerVisible = showHeader !== 'false';

  // Empty state
  if (floorplans.length === 0) {
    return (
      <div className="py-16 text-center bg-muted" style={{ color: 'var(--vibe-color-text-muted)' }}>
        <div className="mx-auto max-w-md">
          <EditableBlockToken
            localName="title"
            label="Section Title"
            defaultValue="Floor Plans"
            as="h3"
            className="mb-2 text-lg font-medium"
          >
            {title || 'Floor Plans'}
          </EditableBlockToken>
          <p className="text-sm">
            No floor plans are available for this property.
          </p>
        </div>
      </div>
    );
  }

  // Editorial layout: side-by-side image and info
  if (layout === 'editorial') {
    return (
      <section className={cn('py-24 md:py-40 px-6 md:px-16 bg-muted')}>
        {headerVisible && (
          <div className="mb-16 md:mb-24 text-center">
            <EditableBlockToken
              localName="title"
              label="Section Title"
              defaultValue="Floor Plans"
              as="h2"
              className={cn(
                'font-heading font-normal',
                'text-4xl md:text-7xl leading-none',
                'text-primary',
                'm-0 mb-6 tracking-tight'
              )}
            >
              {title}
            </EditableBlockToken>
            {subtitle && (
              <EditableBlockToken
                localName="subtitle"
                label="Section Subtitle"
                as="p"
                className={cn(
                  'text-[0.65rem] font-semibold',
                  'tracking-[0.2em] uppercase',
                  'text-accent'
                )}
              >
                {subtitle}
              </EditableBlockToken>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-16 md:gap-y-24 gap-x-8 md:gap-x-16">
          {floorplans.map((plan, index) => (
            <div
              key={`${plan.floorplan_id || 'plan'}-${index}`}
              className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12 items-start"
            >
              {/* Floorplan Image */}
              <div className={cn(
                'aspect-square',
                'bg-background border border-border',
                'p-4 md:p-8',
                'flex items-center justify-center'
              )}>
                {plan.floorplan_image_url ? (
                  <VibesitesImage
                    url={plan.floorplan_image_url}
                    alt={plan.floorplan_name || ''}
                    width={640}
                    height={640}
                    sizes="(min-width: 640px) 25vw, 100vw"
                    objectFit="contain"
                    className="max-w-full max-h-full object-contain mix-blend-multiply opacity-80"
                  />
                ) : (
                  <div className="text-xs text-muted-foreground">[ Plan ]</div>
                )}
              </div>

              {/* Floorplan Info */}
              <div>
                <h3 className={cn(
                  'text-xl md:text-2xl font-heading',
                  'm-0 mb-2',
                  'text-primary'
                )}>
                  {plan.floorplan_name}
                </h3>
                <div className={cn(
                  'text-xs tracking-widest uppercase',
                  'text-muted-foreground',
                  'mb-6'
                )}>
                  {plan.floorplan_beds_min} BR / {plan.floorplan_baths_min} BA — {plan.floorplan_sqft_min} SF
                </div>
                <p className={cn(
                  'text-[0.9375rem] leading-relaxed font-light',
                  'text-foreground',
                  'm-0 mb-8'
                )}>
                  A thoughtfully proportioned residence balancing light and space.
                </p>
                <a
                  href={`/apartments/${plan.floorplan_id}`}
                  className={cn(
                    'text-[0.65rem] font-semibold',
                    'tracking-[0.15em] uppercase',
                    'text-primary no-underline',
                    'border-b border-accent pb-1'
                  )}
                >
                  View Details
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Card layout: standard grid with cards (default)
  return (
    <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-muted">
      <div className="mx-auto max-w-6xl">
        {headerVisible && (
          <div className="mb-8 text-center">
            <EditableBlockToken
              localName="title"
              label="Section Title"
              defaultValue="Floor Plans"
              as="h2"
              className="text-2xl font-bold tracking-tight md:text-3xl mb-2"
              style={{ color: 'var(--vibe-color-text)', fontFamily: 'var(--vibe-font-heading)' }}
            >
              {title}
            </EditableBlockToken>
            {subtitle && (
              <EditableBlockToken
                localName="subtitle"
                label="Section Subtitle"
                as="p"
                className="text-muted-foreground"
              >
                {subtitle}
              </EditableBlockToken>
            )}
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {floorplans.map((plan, i) => {
            const bedrooms = plan.floorplan_beds_min ? parseInt(plan.floorplan_beds_min) : undefined;
            const bathrooms = plan.floorplan_baths_min;
            const minSqft = plan.floorplan_sqft_min;
            const maxSqft = plan.floorplan_sqft_max;
            const minRent = plan.floorplan_price_min ? parseInt(plan.floorplan_price_min) : undefined;

            return (
              <Card key={plan.floorplan_id || i} className="overflow-hidden group">
                {plan.floorplan_image_url && (
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    <VibesitesImage
                      url={plan.floorplan_image_url}
                      alt={plan.floorplan_name || 'Floor plan'}
                      fill
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      objectFit="cover"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-lg">{plan.floorplan_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                    {bedrooms !== undefined && (
                      <span>{bedrooms === 0 ? 'Studio' : `${bedrooms} Bed`}</span>
                    )}
                    {bathrooms && <span>{bathrooms} Bath</span>}
                    {minSqft && (
                      <span>
                        {minSqft}
                        {maxSqft && maxSqft !== minSqft ? `-${maxSqft}` : ''} sq ft
                      </span>
                    )}
                  </div>
                  {minRent && (
                    <p className="text-xl font-semibold text-primary">
                      From ${minRent.toLocaleString()}/mo
                    </p>
                  )}
                  <Button variant="outline" className="w-full mt-4" asChild>
                    <a href={`/apartments?floorplan=${plan.floorplan_id || i}`}>View Details</a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansBlock',
  displayName: 'Floor Plans',
  tokens: {
    title: {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'Floor Plans',
      description: 'The main headline for the floor plans section.',
    },
    subtitle: {
      label: 'Section Subtitle',
      type: 'text',
      defaultValue: '',
      description: 'Optional subtitle/label below the title.',
    },
    layout: {
      label: 'Layout',
      type: 'text',
      defaultValue: 'card',
      description: 'Layout variant: "card" (grid of cards) or "editorial" (side-by-side image/info).',
    },
    limit: {
      label: 'Max Items',
      type: 'text',
      defaultValue: '6',
      description: 'Maximum number of floor plans to display. Set to 0 for all.',
    },
    show_header: {
      label: 'Show Header',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "false" to hide the title and subtitle.',
    },
  },
};

// ============================================================================
// Block Definition
// ============================================================================

export const floorplansBlock: BlockDefinition = {
  id: 'floorplans',
  name: 'Floor Plans',
  description: 'Display property floor plans in a card grid or editorial layout with images, pricing, and details.',
  category: 'Content',
  component: FloorplansComponent,
  contentSchema,
  requirements: defineComponentRequirements('FloorplansBlock', {
    designTokens: [
      'color.primary',
      'color.surface',
      'color.background',
      'color.border',
      'color.accent',
      'color.text',
      'color.textMuted',
      'font.heading',
    ],
    dataTokens: [
      collections.required('floorplans', 'Floor plan listings'),
    ],
  }),
  tags: ['floorplans', 'apartments', 'units', 'listings', 'pricing', 'floor-plans'],
  defaultGridSpan: 12,
};
